import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Tabs,
  Form,
  InputGroup,
  Button,
  Tab,
  Dropdown,
} from "react-bootstrap";
import Toast from "react-bootstrap/Toast";
import ToastContainer from "react-bootstrap/ToastContainer";
import "bootstrap/dist/css/bootstrap.min.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';

import "../App.css";
import { ResetAllButton } from "./ResetAllButton";
import { getMissionByNode } from "../mission/missions";
import { clearDalleOutputs } from "./dalleOutputsStorage";

function BaseApp({ config, components }) {
  const { Gpt, Dalle, PosterEditor, Voice, Video } = components;

  const [errorMessage, setErrorMessage] = React.useState(null);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [checkingAuth, setCheckingAuth] = React.useState(true);
  const [openaiApiKeyGpt4, setOpenaiApiKeyGpt4] = React.useState('');
  const [gptModel, setGptModel] = React.useState('');
  const [imageModel, setImageModel] = React.useState('');
  const [openaiApiKeyDalle3, setOpenaiApiKeyDalle3] = React.useState('');
  const [nodeId, setNodeId] = React.useState(-1);
  const [nodeIdDone, setNodeIdDone] = React.useState(false);
  const [workshopId, setWorkshopId] = React.useState("unknown");
  const [allWorkshopIds, setAllWorkshopIds] = React.useState([]);
  const [readyForPoster, setReadyForPosterHelper] = React.useState(false);
  const [elevenlabsKeys, setElevenlabsKeys] = React.useState([]);
  const [voiceOver, setVoiceOver] = React.useState('');
  const [elevatorPitch, setElevatorPitch] = React.useState('');
  const [imageGenerationStarted, setImageGenerationStartedHelper] = React.useState(false);
  const [dallePromptText, setDallePromptText] = React.useState('');
  const [dalleSourcePromptIndex, setDalleSourcePromptIndex] = React.useState(null);
  const [videoPromptText, setVideoPromptText] = React.useState('');
  const [videoReferenceImageUrl, setVideoReferenceImageUrl] = React.useState(null);
  const [pendingGptPrompt, setPendingGptPrompt] = React.useState(null);
  const [videoGenerationStarted, setVideoGenerationStarted] = React.useState(false);
  const [posterModeUnlocked, setPosterModeUnlocked] = React.useState(false);

  const [activeTab, setActiveTabState] = useState(() => {
    const saved = localStorage.getItem(`${config.workshopType}-active-tab`);
    return saved || 'gpt';
  });
  const setActiveTab = (tab, { scrollPosition } = {}) => {
    localStorage.setItem(`${config.workshopType}-active-tab`, tab);
    setActiveTabState(tab);
    const defaultScroll = tab === 'poster' ? 'top' : 'bottom';
    const scroll = scrollPosition || defaultScroll;
    setTimeout(() => {
      const top = scroll === 'top' ? 0 : document.documentElement.scrollHeight;
      window.scrollTo({ top, behavior: 'smooth' });
    }, 50);
  };
  const [isMobile, setIsMobile] = useState(false);
  const [showMissionPopup, setShowMissionPopup] = useState(false);
  const [missionPopupClosing, setMissionPopupClosing] = useState(false);
  const [showStarfieldBrief, setShowStarfieldBrief] = useState(false);
  const [transitionPhase, setTransitionPhase] = useState(0);
  const [showFilmTransition, setShowFilmTransition] = useState(false);
  const [filmTransitionPhase, setFilmTransitionPhase] = useState(0);
  const [missionData, setMissionData] = useState(null);

  const fabricCanvasRef = useRef(null);
  const stickyHeaderRef = useRef(null);
  const missionThemeRef = useRef(null);

  const nodeTypeRefDesktop = useRef(null);
  const nodeIdRefDesktop = useRef(null);
  const workshopNameRefDesktop = useRef(null);
  const nodeTypeRefMobile = useRef(null);
  const nodeIdRefMobile = useRef(null);
  const workshopNameRefMobile = useRef(null);

  const setReadyForPoster = (value) => {
    localStorage.setItem(config.storageKeys.readyForPoster, JSON.stringify(value));
    setReadyForPosterHelper(value);
  }

  const setImageGenerationStarted = (value) => {
    localStorage.setItem(config.storageKeys.imageGenerationStarted, JSON.stringify(value));
    setImageGenerationStartedHelper(value);
  }

  const getTabTitle = (tabKey) => {
    const tabTitles = {
      'gpt': 'Text',
      'dalle': 'Image',
      'poster': config.tabs?.poster || 'Poster',
      'voice-cloning': 'Voice',
      'video': 'Video'
    };
    return tabTitles[tabKey] || 'Text';
  }

  useEffect(() => {
    if (config.pageTitle) {
      document.title = config.pageTitle;
    }
  }, [config.pageTitle]);

  useEffect(() => {
    const storedPassword = localStorage.getItem(config.storageKeys.password);

    const storedNode = JSON.parse(localStorage.getItem(config.storageKeys.node));
    if (storedNode) {
      setNodeId(storedNode);
      setNodeIdDone(true);

      if (config.workshopType === 'mission' && config.storageKeys.missionData) {
        const storedMission = JSON.parse(localStorage.getItem(config.storageKeys.missionData));
        if (storedMission) {
          setMissionData(storedMission);
        } else {
          const looked = getMissionByNode(storedNode);
          if (looked) {
            setMissionData(looked);
            localStorage.setItem(config.storageKeys.missionData, JSON.stringify(looked));
          }
        }
      }
    }

    const storedWorkshopId = JSON.parse(localStorage.getItem(config.storageKeys.workshopId));
    if (storedWorkshopId) {
      setWorkshopId(storedWorkshopId);
    }

    const storedReadyForPoster = JSON.parse(localStorage.getItem(config.storageKeys.readyForPoster));
    if (storedReadyForPoster) {
      setReadyForPoster(storedReadyForPoster);
    }

    const storedImageGenerationStarted = JSON.parse(localStorage.getItem(config.storageKeys.imageGenerationStarted));
    if (storedImageGenerationStarted) {
      setImageGenerationStarted(storedImageGenerationStarted);
    }

    if (storedPassword) {
      authenticate(storedPassword);
    } else {
      setCheckingAuth(false);
    }

    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1250);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const navTabs = document.querySelector('.blue-tabs .nav-tabs');
    if (navTabs) {
      if (activeTab) {
        navTabs.classList.add('active-line');
      } else {
        navTabs.classList.remove('active-line');
      }
    }
  }, [activeTab]);

  useEffect(() => {
    if ((config.workshopType === 'mission' || config.workshopType === 'film') && stickyHeaderRef.current && missionThemeRef.current) {
      const updateHeight = () => {
        const h = stickyHeaderRef.current.getBoundingClientRect().height;
        missionThemeRef.current.style.setProperty('--sticky-header-height', `${h}px`);
      };
      updateHeight();
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }
  }, [nodeIdDone, isAuthenticated]);

  function resetAll() {
    localStorage.setItem(config.storageKeys.gptMessages, JSON.stringify([]));
    localStorage.setItem(config.storageKeys.dalleOutputs, JSON.stringify([]));
    localStorage.setItem('film-dalle-outputs', JSON.stringify([]));
    localStorage.setItem('mission-dalle-outputs', JSON.stringify([]));
    localStorage.setItem(config.storageKeys.musicgenUrls, JSON.stringify([]));
    localStorage.setItem(config.storageKeys.workshopId, JSON.stringify(null));
    localStorage.setItem(config.storageKeys.node, JSON.stringify(null));
    localStorage.setItem(config.storageKeys.slide, JSON.stringify(null));
    localStorage.setItem(config.storageKeys.readyForPoster, JSON.stringify(false));
    localStorage.setItem(config.storageKeys.imageGenerationStarted, JSON.stringify(false));
    localStorage.setItem('film-image-generation-started', JSON.stringify(false));
    localStorage.setItem('mission-image-generation-started', JSON.stringify(false));
    localStorage.setItem(config.storageKeys.voices, JSON.stringify([]));
    localStorage.setItem(config.storageKeys.synth, JSON.stringify([]));
    if (config.storageKeys.missionData) {
      localStorage.removeItem(config.storageKeys.missionData);
    }
    localStorage.removeItem(config.storageKeys.password);
    localStorage.removeItem(`${config.workshopType}-active-tab`);
    localStorage.removeItem("poster-text-inputs");
    localStorage.removeItem("slide");
    localStorage.removeItem("voices");
    localStorage.removeItem("generated-videos");
    setPosterModeUnlocked(false);
    localStorage.removeItem('mission-dalle-poster-mode');
    localStorage.removeItem('mission-image-rating');
    if (config.gpt?.budgetTable?.storageKey) {
      localStorage.removeItem(config.gpt.budgetTable.storageKey);
      localStorage.removeItem(config.gpt.budgetTable.storageKey + "-ai");
      localStorage.removeItem(config.gpt.budgetTable.storageKey + "-return");
      localStorage.removeItem(config.gpt.budgetTable.storageKey + "-roi");
    }
    // Clear image generation from IndexedDB (same as Video tab uses localStorage) so Image Generation tab is reset
    Promise.all([
      clearDalleOutputs('film-dalle-outputs'),
      clearDalleOutputs('mission-dalle-outputs'),
    ]).then(() => {
      window.location.href = '/portal';
    }).catch(() => {
      window.location.href = '/portal';
    });
  }

  function authenticate(passwordValue) {
    fetch("/authenticate_frontend", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ password: passwordValue }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.authenticated) {
          setIsAuthenticated(true);
          setOpenaiApiKeyGpt4(data.openai_api_key_gpt4);
          setOpenaiApiKeyDalle3(data.openai_api_key_dalle3);
          setGptModel(data.gpt_model);
          setImageModel(data.image_model);
          setAllWorkshopIds(data.workshop_ids);
          setElevenlabsKeys(data.elevenlabs_keys);
          localStorage.setItem(config.storageKeys.password, passwordValue);
        } else {
          setIsAuthenticated(false);
        }
        setCheckingAuth(false);
      })
      .catch((error) => {
        console.error("Authentication request error:", error);
        setCheckingAuth(false);
      });
  }

  const openManageServers = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const host = window.location.hostname;
    const port = window.location.port;
    const isLocalDev = (host === 'localhost' || host === '127.0.0.1') && (port === '3000' || port === '3001');
    const managePort = port === '3001' ? '3000' : '3001';
    const manageUrl = isLocalDev
      ? `http://${host}:${managePort}/manage-servers/?type=${config.workshopType}`
      : `/manage-servers/?type=${config.workshopType}`;
    window.open(manageUrl, '_blank', 'noopener,noreferrer');
  };

  let errorMessageToast = <></>;
  if (errorMessage) {
    errorMessageToast = (
      <ToastContainer position="bottom-center" containerPosition="fixed" style={{ zIndex: 99999 }}>
        <Toast
          show={errorMessage != null}
          onClose={() => setErrorMessage(null)}
          style={{ marginBottom: "90px", backgroundColor: "#dddddd", zIndex: 99999 }}
        >
          <Toast.Header>
            <strong className="me-auto" style={{ fontSize: 'large' }}>Error</strong>
          </Toast.Header>
          <Toast.Body style={{ textAlign: "center" }}>
            {errorMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    );
  }

  if (checkingAuth) {
    return null;
  }

  if (!isAuthenticated) {
    window.location.href = '/portal';
    return null;
  }

  if (!nodeIdDone) {
    return (
      <div className={config.nodePageClass || 'intro-page-background'}>
        {config.nodePageClass === 'intro-page-background-mission' && (
          <>
            <div className="starfield-layer starfield-layer-1"></div>
            <div className="starfield-layer starfield-layer-2"></div>
            <div className="starfield-layer starfield-layer-3"></div>
            <div className="starfield-layer starfield-layer-4"></div>
            <div className="starfield-layer starfield-layer-5"></div>
            <div className="starfield-layer starfield-layer-6"></div>
          </>
        )}
        {showMissionPopup && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 99999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}>
            <div className="mission-popup-card" style={{
              width: '80vw',
              height: '80vh',
              backgroundColor: 'white',
              borderRadius: '0.4rem',
              overflow: 'hidden',
              border: '3px solid #f05f40',
              boxShadow: '0 0 30px rgba(240, 95, 64, 0.5)',
              position: 'relative',
              zIndex: 2,
              display: 'flex',
              flexDirection: 'column',
              transition: 'transform 0.2s ease-in, opacity 0.2s ease-in',
              transform: missionPopupClosing ? 'scale(0)' : 'scale(1)',
              opacity: missionPopupClosing ? 0 : 1,
            }}>
              <div className="mission-popup-content" style={{
                padding: 'min(4vh, 3.5rem) min(4vw, 4rem)',
                textAlign: 'center',
                fontFamily: 'Roboto, sans-serif',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <p style={{
                  fontSize: 'min(2.2vw, 3vh, 1.4rem)',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: 'clamp(1px, 0.3vw, 3px)',
                  color: '#555',
                  marginBottom: 'min(4.5vh, 3.5rem)'
                }}>
                  Your team has been assigned the historic US space mission
                </p>
                <div style={{
                  display: 'inline-block',
                  border: '2px solid #f05f40',
                  borderRadius: '0.4rem',
                  padding: 'min(1.5vh, 1.2rem) min(3vw, 3rem)',
                  marginBottom: 'min(4.5vh, 3.5rem)',
                  boxShadow: '0 0 12px rgba(240, 95, 64, 0.3)'
                }}>
                  <span style={{
                    fontSize: 'min(4.5vw, 6vh, 3rem)',
                    fontWeight: 'bold',
                    color: '#f05f40',
                    letterSpacing: 'clamp(1px, 0.2vw, 2px)',
                    display: 'block'
                  }}>
                    {missionData ? missionData.name : `MISSION ${nodeId}`}
                  </span>
                  {missionData && (
                    <>
                      <span style={{
                        fontSize: 'min(2vw, 2.5vh, 1.3rem)',
                        color: '#999',
                        display: 'block',
                        marginTop: 'min(0.4vh, 0.3rem)',
                        letterSpacing: '1px'
                      }}>
                        {missionData.date}
                      </span>
                      <span style={{
                        fontSize: 'min(1.8vw, 2.2vh, 1.2rem)',
                        color: '#aaa',
                        display: 'block',
                        marginTop: 'min(0.3vh, 0.2rem)',
                        fontStyle: 'italic'
                      }}>
                        {missionData.significance}
                      </span>
                    </>
                  )}
                </div>
                <p style={{
                  fontSize: 'min(2vw, 2.8vh, 1.5rem)',
                  lineHeight: '1.8',
                  color: '#888',
                  maxWidth: '80%',
                  margin: '0 auto min(4.5vh, 3.5rem) auto'
                }}>
                  This mission changed what was possible. Now it's your turn to study it,
                  uncover what made it succeed, and design the next generation successor
                  using the latest AI models.
                </p>
                <button
                  type="button"
                  className="mission-start-btn"
                  style={{
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    backgroundColor: '#0d6efd',
                    border: '2px solid #0d6efd',
                    color: '#fff',
                    fontSize: 'min(2vw, 2.8vh, 1.4rem)',
                    padding: 'min(1vh, 0.8rem) min(3vw, 3rem)',
                    borderRadius: '2rem',
                    letterSpacing: '1px',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    transition: 'all 0.3s ease',
                    boxShadow: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Roboto, sans-serif',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#0b5ed7';
                    e.currentTarget.style.borderColor = '#0b5ed7';
                    e.currentTarget.style.transform = 'scale(1.07)';
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(13, 110, 253, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#0d6efd';
                    e.currentTarget.style.borderColor = '#0d6efd';
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onClick={() => {
                    setMissionPopupClosing(true);
                    setTimeout(() => {
                      setShowMissionPopup(false);
                      setMissionPopupClosing(false);
                      setShowStarfieldBrief(true);
                      setTransitionPhase(1);
                      setTimeout(() => {
                        setTransitionPhase(2);
                      }, 50);
                      setTimeout(() => {
                        setTransitionPhase(3);
                      }, 600);
                      setTimeout(() => {
                        setShowStarfieldBrief(false);
                        setTransitionPhase(0);
                        setNodeIdDone(true);
                      }, 1100);
                    }, 200);
                  }}
                >
                  Start Mission
                </button>
              </div>
            </div>
          </div>
        )}
        {showStarfieldBrief && config.workshopType === 'mission' && missionData && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99998,
            pointerEvents: 'none',
          }}>
            <div className="mission-theme" style={{ minHeight: '100vh' }}>
              <div className="sticky-header">
                <Container>
                  <Row className="mb-0" style={{ paddingTop: '14px' }}>
                    <Col>
                      <div style={{
                        opacity: transitionPhase >= 2 ? 1 : 0,
                        transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}>
                        <div className="mission-banner" style={{ marginBottom: '14px' }}>
                          <span className="mission-banner-line1">
                            <span className="mission-banner-name">{missionData.name}</span>
                            <span className="mission-banner-sep">&nbsp;&nbsp;({missionData.date}) <span className="mission-banner-dash">&mdash; </span></span>
                          </span>
                          <span className="mission-banner-sig">{missionData.significance}</span>
                        </div>
                        <div className="orange-header-container" style={{
                          backgroundColor: '#f05f40',
                          padding: '0 0.4rem',
                          borderRadius: '0.4rem 0.4rem 0 0',
                          marginBottom: '0',
                          height: '2.2rem',
                          display: 'flex',
                          alignItems: 'center',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}>
                              <span style={{ color: 'white', fontSize: '1.4rem', fontWeight: 'bold', lineHeight: '1.2' }}>
                                {config.heading}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', flex: 1, height: '100%', marginBottom: '0px', paddingBottom: '0px' }}>
                              <Tabs
                                activeKey=""
                                className="inline-tabs"
                                style={{ margin: '0', padding: '0', marginBottom: '0px', transform: 'translateY(calc(0.3rem - 1px))' }}
                              >
                                <Tab eventKey="gpt" title={<>Text</>}></Tab>
                                <Tab eventKey="dalle" title={<>Image</>}></Tab>
                                <Tab eventKey="voice-cloning" title={<>Voice</>}></Tab>
                                <Tab eventKey="video" title={<>Video</>}></Tab>
                              </Tabs>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end', height: '100%', paddingBottom: '0.2rem' }}>
                              <span className="node-text" style={{ color: 'white', marginTop: '0px', whiteSpace: 'nowrap', marginBottom: '2px' }}>{nodeId}</span>
                              <span className="workshop-text" style={{ color: 'white', fontSize: '0.5rem', fontWeight: 'normal', fontFamily: 'Roboto, sans-serif', lineHeight: '1', marginTop: '0px' }}>{workshopId}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </div>

              <div style={{
                opacity: transitionPhase >= 2 ? 1 : 0,
                transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              }}>
                <Container>
                  <Row style={{ marginTop: '0px' }}>
                    <Col>
                      <div style={{
                        height: transitionPhase >= 3 ? '300px' : '20px',
                        transition: 'height 0.5s ease-out',
                        backgroundColor: '#fff7ef',
                      }} />
                      <div className="bottom-tabs-container" style={{
                        backgroundColor: '#f05f40',
                        padding: '0.4rem 0.4rem 0 0.4rem',
                        borderRadius: '0 0 0.4rem 0.4rem',
                        marginBottom: '0.25rem',
                        height: '2.2rem',
                        position: 'relative',
                        zIndex: 2,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>
                          <div className="spacer-left" style={{ display: 'flex', alignItems: 'center', textAlign: 'left', backgroundColor: '#f05f40', height: '1.4rem' }}>
                            <span style={{ color: 'transparent', fontSize: '1.4rem', fontWeight: 'bold', lineHeight: '1.2' }}>
                              {config.heading}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flex: 1, height: '100%', marginBottom: '0px', position: 'relative', zIndex: 1 }}>
                            <Tabs
                              activeKey=""
                              className="bottom-tabs"
                              style={{
                                margin: '0',
                                padding: '0',
                                marginBottom: '0px',
                                transform: 'translateY(-0.4rem)',
                                '--bs-border-radius': '0.4rem',
                                '--bs-border-top-left-radius': '0',
                                '--bs-border-top-right-radius': '0',
                                '--bs-border-bottom-left-radius': '0.4rem',
                                '--bs-border-bottom-right-radius': '0.4rem'
                              }}
                            >
                              <Tab eventKey="gpt" title={<>Text</>}></Tab>
                              <Tab eventKey="dalle" title={<>Image</>}></Tab>
                              <Tab eventKey="voice-cloning" title={<>Voice</>}></Tab>
                              <Tab eventKey="video" title={<>Video</>}></Tab>
                            </Tabs>
                          </div>
                          <div className="spacer-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end', backgroundColor: '#f05f40', height: '1.4rem', paddingBottom: '0.2rem' }}>
                            <span className="node-text" style={{ color: 'transparent', whiteSpace: 'nowrap', marginBottom: '2px' }}>{nodeId}</span>
                            <span className="workshop-text" style={{ color: 'transparent', fontSize: '0.5rem', fontWeight: 'normal', fontFamily: 'Roboto, sans-serif', lineHeight: '1' }}>{workshopId}</span>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </div>
            </div>
          </div>
        )}
        {showFilmTransition && config.workshopType !== 'mission' && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99998,
            pointerEvents: 'none',
            backgroundColor: '#f5f5f5',
          }}>
            <div className="film-theme" style={{ minHeight: '100vh' }}>
              <div className="sticky-header">
                <Container>
                  <Row className="mb-0" style={{ paddingTop: '8px' }}>
                    <Col>
                      <div style={{
                        opacity: filmTransitionPhase >= 2 ? 1 : 0,
                        transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}>
                        <div className="orange-header-container" style={{
                          backgroundColor: '#f05f40',
                          padding: '0 0.4rem',
                          borderRadius: '0.4rem 0.4rem 0 0',
                          marginBottom: '0',
                          height: '2.2rem',
                          display: 'flex',
                          alignItems: 'center',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <div style={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}>
                              <span style={{ color: 'white', fontSize: '1.4rem', fontWeight: 'bold', lineHeight: '1.2' }}>
                                {config.heading}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', flex: 1, height: '100%', marginBottom: '0px', paddingBottom: '0px' }}>
                              <Tabs
                                activeKey=""
                                className="inline-tabs"
                                style={{ margin: '0', padding: '0', marginBottom: '0px', transform: 'translateY(calc(0.3rem - 1px))' }}
                              >
                                <Tab eventKey="gpt" title={<>Text</>}></Tab>
                                <Tab eventKey="dalle" title={<>Image</>}></Tab>
                                <Tab eventKey="voice-cloning" title={<>Voice</>}></Tab>
                                <Tab eventKey="video" title={<>Video</>}></Tab>
                              </Tabs>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end', height: '100%', paddingBottom: '0.2rem' }}>
                              <span className="node-text" style={{ color: 'white', marginTop: '0px', whiteSpace: 'nowrap', marginBottom: '2px' }}>{nodeId}</span>
                              <span className="workshop-text" style={{ color: 'white', fontSize: '0.5rem', fontWeight: 'normal', fontFamily: 'Roboto, sans-serif', lineHeight: '1', marginTop: '0px' }}>{workshopId}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </div>

              <div style={{
                opacity: filmTransitionPhase >= 2 ? 1 : 0,
                transition: 'opacity 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
              }}>
                <Container>
                  <Row style={{ marginTop: '0px' }}>
                    <Col>
                      <div style={{
                        height: filmTransitionPhase >= 3 ? '300px' : '20px',
                        transition: 'height 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        backgroundColor: '#fff7ef',
                      }} />
                      <div className="bottom-tabs-container" style={{
                        backgroundColor: '#f05f40',
                        padding: '0.4rem 0.4rem 0 0.4rem',
                        borderRadius: '0 0 0.4rem 0.4rem',
                        marginBottom: '0.25rem',
                        height: '2.2rem',
                        position: 'relative',
                        zIndex: 2,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>
                          <div className="spacer-left" style={{ display: 'flex', alignItems: 'center', textAlign: 'left', backgroundColor: '#f05f40', height: '1.4rem' }}>
                            <span style={{ color: 'transparent', fontSize: '1.4rem', fontWeight: 'bold', lineHeight: '1.2' }}>
                              {config.heading}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flex: 1, height: '100%', marginBottom: '0px', position: 'relative', zIndex: 1 }}>
                            <Tabs
                              activeKey=""
                              className="bottom-tabs"
                              style={{
                                margin: '0',
                                padding: '0',
                                marginBottom: '0px',
                                transform: 'translateY(-0.4rem)',
                                '--bs-border-radius': '0.4rem',
                                '--bs-border-top-left-radius': '0',
                                '--bs-border-top-right-radius': '0',
                                '--bs-border-bottom-left-radius': '0.4rem',
                                '--bs-border-bottom-right-radius': '0.4rem'
                              }}
                            >
                              <Tab eventKey="gpt" title={<>Text</>}></Tab>
                              <Tab eventKey="dalle" title={<>Image</>}></Tab>
                              <Tab eventKey="voice-cloning" title={<>Voice</>}></Tab>
                              <Tab eventKey="video" title={<>Video</>}></Tab>
                            </Tabs>
                          </div>
                          <div className="spacer-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end', backgroundColor: '#f05f40', height: '1.4rem', paddingBottom: '0.2rem' }}>
                            <span className="node-text" style={{ color: 'transparent', whiteSpace: 'nowrap', marginBottom: '2px' }}>{nodeId}</span>
                            <span className="workshop-text" style={{ color: 'transparent', fontSize: '0.5rem', fontWeight: 'normal', fontFamily: 'Roboto, sans-serif', lineHeight: '1' }}>{workshopId}</span>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Container>
              </div>
            </div>
          </div>
        )}
        <div style={{
          backgroundColor: '#f05f40',
          padding: '5px 0px',
          width: '100%',
          position: 'relative',
          zIndex: 10,
          display: (showMissionPopup || showStarfieldBrief || showFilmTransition) ? 'none' : undefined,
        }}>
          <Container fluid>
            <div style={{ display: 'flex', alignItems: 'center', textAlign: 'left', paddingLeft: '0px' }}>
              <span style={{ color: 'white', fontSize: '24px', fontWeight: 'bold', lineHeight: '1.2', fontFamily: 'Roboto, sans-serif' }}>
                {config.heading}
              </span>
            </div>
          </Container>
        </div>

        <div className="centered-content" style={{ display: (showMissionPopup || showStarfieldBrief || showFilmTransition) ? 'none' : undefined }}>
          <div className="main-content-container">
            <Container className="mt-4">
              <Row className="mb-4">
                <Col>
                  <h2>My neural network node was:</h2>
                  <p>(the node you had during the paper exercise)</p>
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      const isCurrentlyMobile = window.innerWidth < 1250;

                      let wid, nodeval, nodetype;

                      if (isCurrentlyMobile) {
                        wid = workshopNameRefMobile.current.value;
                        nodeval = nodeIdRefMobile.current.value;
                        nodetype = nodeTypeRefMobile.current.value;
                      } else {
                        wid = workshopNameRefDesktop.current.value;
                        nodeval = nodeIdRefDesktop.current.value;
                        nodetype = nodeTypeRefDesktop.current.value;
                      }

                      if (wid === "" || nodeval === "" || nodetype === "") {
                        setErrorMessage("Select a workshop ID and enter a node number.");
                        return;
                      }
                      if (config.workshopType === 'mission') {
                        const num = parseInt(nodeval, 10);
                        const maxNode = nodetype === 'B' ? 45 : 25;
                        if (num < 1 || num > maxNode) {
                          setErrorMessage(`Node ${nodetype} must be between 1 and ${maxNode}.`);
                          return;
                        }
                      }
                      const fullNode = nodetype + nodeval;
                      setWorkshopId(wid);
                      setNodeId(fullNode);
                      localStorage.setItem(config.storageKeys.node, JSON.stringify(fullNode));
                      localStorage.setItem(config.storageKeys.workshopId, JSON.stringify(wid));
                      if (config.workshopType === 'mission') {
                        const mission = getMissionByNode(fullNode);
                        if (!mission) {
                          setErrorMessage(`No mission found for node ${fullNode}.`);
                          return;
                        }
                        setMissionData(mission);
                        localStorage.setItem(config.storageKeys.missionData, JSON.stringify(mission));
                        setShowMissionPopup(true);
                      } else {
                        setShowFilmTransition(true);
                        setFilmTransitionPhase(1);
                        setTimeout(() => {
                          setFilmTransitionPhase(2);
                        }, 100);
                        setTimeout(() => {
                          setFilmTransitionPhase(3);
                        }, 700);
                        setTimeout(() => {
                          setShowFilmTransition(false);
                          setFilmTransitionPhase(0);
                          setNodeIdDone(true);
                        }, 1400);
                      }
                    }}
                  >
                    <div className="d-none d-lg-block">
                      <InputGroup>
                        <Form.Select aria-label="Workshop select"
                          style={{ maxWidth: '600px', marginRight: '10px' }}
                          ref={workshopNameRefDesktop}>
                          <option value="">-- Select a workshop --</option>
                          {allWorkshopIds.map((id) => (
                            <option key={id} value={id}>
                              {id}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Select aria-label="Node type select"
                          style={{ maxWidth: '200px', marginRight: '10px' }}
                          ref={nodeTypeRefDesktop}>
                          <option value="B">B</option>
                          <option value="C">C</option>
                        </Form.Select>
                        <Form.Control
                          type="number"
                          placeholder="Enter number"
                          min={config.workshopType === 'mission' ? "1" : "0"}
                          max={config.workshopType === 'mission' ? "45" : "100"}
                          style={{ maxWidth: '200px' }}
                          ref={nodeIdRefDesktop}
                        />
                        <Button type="submit">Go</Button>
                      </InputGroup>
                    </div>

                    <div className="d-lg-none">
                      <div className="mb-3">
                        <Form.Select aria-label="Workshop select"
                          style={{ width: '100%' }}
                          ref={workshopNameRefMobile}>
                          <option value="">-- Select a workshop --</option>
                          {allWorkshopIds.map((id) => (
                            <option key={id} value={id}>
                              {id}
                            </option>
                          ))}
                        </Form.Select>
                      </div>
                      <div className="mb-3">
                        <Form.Select aria-label="Node type select"
                          style={{ width: '100%' }}
                          ref={nodeTypeRefMobile}>
                          <option value="B">B</option>
                          <option value="C">C</option>
                        </Form.Select>
                      </div>
                      <div className="mb-3">
                        <Form.Control
                          type="number"
                          placeholder="Enter number"
                          min={config.workshopType === 'mission' ? "1" : "0"}
                          max={config.workshopType === 'mission' ? "45" : "100"}
                          style={{ width: '100%' }}
                          ref={nodeIdRefMobile}
                        />
                      </div>
                      <div className="mb-3">
                        <Button type="submit" className="w-100 node-go-btn">Go</Button>
                      </div>
                    </div>
                  </Form>
                </Col>
              </Row>
              {errorMessageToast}
            </Container>
          </div>
        </div>

        {config.workshopType !== 'mission' && (
          <div className="film-posters-text-box" style={{
            position: 'fixed',
            bottom: 'min(2vh, 20px)',
            right: 'min(2vw, 20px)',
            backgroundColor: 'white',
            border: '2px solid gray',
            borderRadius: '50px',
            padding: 'min(0.4vh, 5px) min(0.6vw, 8px)',
            zIndex: 1000,
            maxWidth: '90vw',
          }}>
            <div style={{
              color: 'gray',
              fontFamily: 'Roboto, sans-serif',
              fontSize: 'min(0.8vw, 1vh, 10px)',
              fontWeight: 'bold',
              lineHeight: '1.3',
              textAlign: 'center',
            }}>
              {config.footerBlurb}
            </div>
          </div>
        )}

        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#999999',
          fontSize: '12px',
          fontFamily: 'Roboto, sans-serif',
          fontWeight: 'normal',
          zIndex: 9999,
          whiteSpace: 'nowrap'
        }}>
          © 2026 Stage One Education, LLC
        </div>

        <div
          className="gear-icon"
          onClick={openManageServers}
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '20px',
            fontSize: '20px',
            color: 'white',
            cursor: 'pointer',
            zIndex: 1000,
            opacity: 0.1
          }}
        >
          <FontAwesomeIcon icon={faCog} />
        </div>
      </div>
    )
  }

  let gptNumber = '4';
  if (gptModel.includes('gpt-5')) {
    gptNumber = '5';
  } else if (gptModel.includes('gpt-3.5')) {
    gptNumber = '3.5';
  }

  const posterTabTitle = config.tabs?.poster || 'Poster';
  const showPosterTab = readyForPoster;

  return (
    <div ref={missionThemeRef} className={config.workshopType === 'mission' ? 'mission-theme' : config.workshopType === 'film' ? 'film-theme' : ''}>
      <div ref={stickyHeaderRef} className="sticky-header">
        <Container>
          <Row className="mb-0" style={{ paddingTop: config.workshopType === 'mission' ? '14px' : '8px' }}>
            <Col>
              {config.workshopType === 'mission' && missionData && (
                <div className="mission-banner" style={{ marginBottom: '14px' }}>
                  <span className="mission-banner-line1">
                    <span className="mission-banner-name">{missionData.name}</span>
                    <span className="mission-banner-sep">&nbsp;&nbsp;({missionData.date}) <span className="mission-banner-dash">&mdash; </span></span>
                  </span>
                  <span className="mission-banner-sig">{missionData.significance}</span>
                </div>
              )}
              <div className={`orange-header-container ${isMobile ? 'mobile-header' : ''}`} style={{
                backgroundColor: '#f05f40',
                padding: '0 0.4rem',
                borderRadius: '0.4rem 0.4rem 0 0',
                marginBottom: '0',
                height: isMobile ? '4rem' : '2.2rem',
                display: 'flex',
                alignItems: 'center'
              }}>
                {isMobile ? (
                  <div className="mobile-header-layout" style={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}>
                        <span style={{ color: 'white', fontSize: '1.2rem', fontWeight: 'bold', lineHeight: '1.2' }}>
                          {config.heading}
                        </span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                        <span className="node-text" style={{ color: 'white', marginTop: '0px', whiteSpace: 'nowrap', marginBottom: '2px', fontSize: '0.7rem' }}>{nodeId}</span>
                        <span className="workshop-text" style={{ color: 'white', fontSize: '0.5rem', fontWeight: 'normal', fontFamily: 'Roboto, sans-serif', lineHeight: '1', marginTop: '0px' }}>{workshopId}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                      <Dropdown className="mobile-dropdown">
                        <Dropdown.Toggle variant="outline-light" size="sm" className={`mobile-dropdown-toggle${config.workshopType === 'mission' ? ` tab-color-${activeTab}` : ''}`}>
                          {getTabTitle(activeTab)}
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="mobile-dropdown-menu">
                          <Dropdown.Item active={activeTab === 'gpt'} onClick={() => setActiveTab('gpt')} className="mobile-dropdown-item">Text</Dropdown.Item>
                          <Dropdown.Item active={activeTab === 'dalle'} onClick={() => setActiveTab('dalle')} className="mobile-dropdown-item">Image</Dropdown.Item>
                          {showPosterTab && <Dropdown.Item active={activeTab === 'poster'} onClick={() => setActiveTab('poster')} className="mobile-dropdown-item">{posterTabTitle}</Dropdown.Item>}
                          <Dropdown.Item active={activeTab === 'voice-cloning'} onClick={() => setActiveTab('voice-cloning')} className="mobile-dropdown-item">Voice</Dropdown.Item>
                          <Dropdown.Item active={activeTab === 'video'} onClick={() => setActiveTab('video')} className="mobile-dropdown-item">Video</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', textAlign: 'left' }}>
                      <span style={{ color: 'white', fontSize: '1.4rem', fontWeight: 'bold', lineHeight: '1.2' }}>
                        {config.heading}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', flex: 1, height: '100%', marginBottom: '0px', paddingBottom: '0px' }}>
                      <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        className="inline-tabs"
                        style={{ margin: '0', padding: '0', marginBottom: '0px', transform: 'translateY(calc(0.3rem - 1px))' }}
                      >
                        <Tab eventKey="gpt" title={<>Text</>}></Tab>
                        <Tab eventKey="dalle" title={<>Image</>}></Tab>
                        {showPosterTab && <Tab eventKey="poster" title={<>{posterTabTitle}</>}></Tab>}
                        <Tab eventKey="voice-cloning" title={<>Voice</>}></Tab>
                        <Tab eventKey="video" title={<>Video</>}></Tab>
                      </Tabs>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end', height: '100%', paddingBottom: '0.2rem' }}>
                      <span className="node-text" style={{ color: 'white', marginTop: '0px', whiteSpace: 'nowrap', marginBottom: '2px' }}>{nodeId}</span>
                      <span className="workshop-text" style={{ color: 'white', fontSize: '0.5rem', fontWeight: 'normal', fontFamily: 'Roboto, sans-serif', lineHeight: '1', marginTop: '0px' }}>{workshopId}</span>
                    </div>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <Container>
        <Row className="mb-4" style={{ marginTop: '0px' }}>
          <Col>
            <div className="film-content-body">
            <div className="white-content-area" style={{ backgroundColor: {'gpt': '#fff7ef', 'dalle': '#f3f7fc', 'poster': '#f3f7fc', 'voice-cloning': '#f7f3fc', 'video': '#f3f8f5'}[activeTab] || '#fff7ef', '--outer-bg': {'gpt': '#fff7ef', 'dalle': '#f3f7fc', 'poster': '#f3f7fc', 'voice-cloning': '#f7f3fc', 'video': '#f3f8f5'}[activeTab] || '#fff7ef', padding: '2px 20px 20px 20px', borderRadius: '0px' }}>
                  <div style={{ display: activeTab === 'gpt' ? 'block' : 'none' }}>
                    <Gpt
                      openaiApiKey={openaiApiKeyGpt4}
                      gptModel={gptModel}
                      setErrorMessage={setErrorMessage}
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                      setVoiceOver={setVoiceOver}
                      setElevatorPitch={setElevatorPitch}
                      imageGenerationStarted={imageGenerationStarted}
                      resetAll={resetAll}
                      missionData={missionData}
                      setDallePromptText={setDallePromptText}
                      setDalleSourcePromptIndex={setDalleSourcePromptIndex}
                      pendingGptPrompt={pendingGptPrompt}
                      setPendingGptPrompt={setPendingGptPrompt}
                      videoGenerationStarted={videoGenerationStarted}
                      readyForPoster={readyForPoster}
                      setPosterModeUnlocked={setPosterModeUnlocked}
                      workshopId={workshopId}
                      studentId={nodeId}
                      passwordStorageKey={config.storageKeys.password}
                    />
                  </div>
                  <div style={{ display: activeTab === 'dalle' ? 'block' : 'none' }}>
                    <Dalle
                      openaiApiKey={openaiApiKeyDalle3}
                      setErrorMessage={setErrorMessage}
                      activeTab={activeTab}
                      nodeId={nodeId}
                      workshopId={workshopId}
                      imageModel={imageModel}
                      fabricCanvasRef={fabricCanvasRef}
                      switchTab={(tab) => setActiveTab(tab)}
                      readyForPoster={readyForPoster}
                      setReadyForPoster={setReadyForPoster}
                      setImageGenerationStarted={setImageGenerationStarted}
                      missionData={missionData}
                      dallePromptText={dallePromptText}
                      setDallePromptText={setDallePromptText}
                      dalleSourcePromptIndex={dalleSourcePromptIndex}
                      setVideoPromptText={setVideoPromptText}
                      setVideoReferenceImageUrl={setVideoReferenceImageUrl}
                      setPendingGptPrompt={setPendingGptPrompt}
                      posterModeUnlocked={posterModeUnlocked}
                    />
                  </div>
                  <div style={{ display: activeTab === 'poster' ? 'block' : 'none' }}>
                    <PosterEditor
                      canvasRef={fabricCanvasRef}
                      workshopId={workshopId}
                      studentId={nodeId}
                      passwordStorageKey={config.storageKeys.password}
                      readyForPoster={readyForPoster}
                      activeTab={activeTab}
                      switchTab={setActiveTab}
                      missionData={missionData}
                    />
                  </div>
                  <div style={{ display: activeTab === 'voice-cloning' ? 'block' : 'none' }}>
                    <Voice
                      elevenlabsKeys={elevenlabsKeys}
                      voiceOver={voiceOver}
                      elevatorPitch={elevatorPitch}
                      activeTab={activeTab}
                      workshopId={workshopId}
                      switchTab={setActiveTab}
                      missionData={missionData}
                      setPendingGptPrompt={setPendingGptPrompt}
                    />
                  </div>
                  <div style={{ display: activeTab === 'video' ? 'block' : 'none' }}>
                    <Video
                      openaiApiKey={openaiApiKeyDalle3}
                      setErrorMessage={setErrorMessage}
                      activeTab={activeTab}
                      switchTab={setActiveTab}
                      videoPromptText={videoPromptText}
                      setVideoPromptText={setVideoPromptText}
                      videoReferenceImageUrl={videoReferenceImageUrl}
                      setVideoReferenceImageUrl={setVideoReferenceImageUrl}
                      missionData={missionData}
                      workshopType={config.workshopType}
                      setVideoGenerationStarted={setVideoGenerationStarted}
                    />
                  </div>
                </div>

            </div>

            <div style={{
              backgroundColor: {'gpt': '#fff7ef', 'dalle': '#f3f7fc', 'poster': '#f3f7fc', 'voice-cloning': '#f7f3fc', 'video': '#f3f8f5'}[activeTab] || '#fff7ef',
              height: '6px',
              position: 'relative',
              zIndex: 3,
              marginTop: '0px',
              marginBottom: '-1px'
            }} />

            <div className="bottom-tabs-container" style={{
              backgroundColor: '#f05f40',
              padding: '0.4rem 0.4rem 0 0.4rem',
              borderRadius: '0 0 0.4rem 0.4rem',
              marginBottom: '0.25rem',
              height: '2.2rem',
              position: 'relative',
              zIndex: 2
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>
                <div className="spacer-left" style={{ display: 'flex', alignItems: 'center', textAlign: 'left', backgroundColor: '#f05f40', height: '1.4rem' }}>
                  <span style={{ color: 'transparent', fontSize: '1.4rem', fontWeight: 'bold', lineHeight: '1.2' }}>
                    {config.heading}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', flex: 1, height: '100%', marginBottom: '0px', position: 'relative', zIndex: 1 }}>
                  {isMobile ? (
                    <Dropdown className="mobile-dropdown-bottom">
                      <Dropdown.Toggle variant="outline-light" size="sm" className="mobile-dropdown-toggle-bottom">
                        {getTabTitle(activeTab)}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="mobile-dropdown-menu-bottom">
                        <Dropdown.Item active={activeTab === 'gpt'} onClick={() => setActiveTab('gpt')} className="mobile-dropdown-item">Text</Dropdown.Item>
                        <Dropdown.Item active={activeTab === 'dalle'} onClick={() => setActiveTab('dalle')} className="mobile-dropdown-item">Image</Dropdown.Item>
                        {showPosterTab && <Dropdown.Item active={activeTab === 'poster'} onClick={() => setActiveTab('poster')} className="mobile-dropdown-item">{posterTabTitle}</Dropdown.Item>}
                        <Dropdown.Item active={activeTab === 'voice-cloning'} onClick={() => setActiveTab('voice-cloning')} className="mobile-dropdown-item">Voice</Dropdown.Item>
                        <Dropdown.Item active={activeTab === 'video'} onClick={() => setActiveTab('video')} className="mobile-dropdown-item">Video</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  ) : (
                    <Tabs
                      activeKey={activeTab}
                      onSelect={(k) => setActiveTab(k)}
                      className="bottom-tabs"
                      style={{
                        margin: '0',
                        padding: '0',
                        marginBottom: '0px',
                        transform: 'translateY(-0.4rem)',
                        '--bs-border-radius': '0.4rem',
                        '--bs-border-top-left-radius': '0',
                        '--bs-border-top-right-radius': '0',
                        '--bs-border-bottom-left-radius': '0.4rem',
                        '--bs-border-bottom-right-radius': '0.4rem'
                      }}
                    >
                      <Tab eventKey="gpt" title={<>Text</>}></Tab>
                      <Tab eventKey="dalle" title={<>Image</>}></Tab>
                      {showPosterTab && <Tab eventKey="poster" title={<>{posterTabTitle}</>}></Tab>}
                      <Tab eventKey="voice-cloning" title={<>Voice</>}></Tab>
                      <Tab eventKey="video" title={<>Video</>}></Tab>
                    </Tabs>
                  )}
                </div>
                <div className="spacer-right" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-end', backgroundColor: '#f05f40', height: '1.4rem', paddingBottom: '0.2rem' }}>
                  <span className="node-text" style={{ color: 'transparent', marginTop: '0px', whiteSpace: 'nowrap', marginBottom: '2px' }}>{nodeId}</span>
                  <span className="workshop-text" style={{ color: 'transparent', fontSize: '0.5rem', fontWeight: 'normal', fontFamily: 'Roboto, sans-serif', lineHeight: '1', marginTop: '0px' }}>{workshopId}</span>
                </div>
              </div>
            </div>
          </Col>
        </Row>
        {errorMessageToast}

        <div className="copyright-button-container" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginTop: '20px',
          marginBottom: '10px',
          paddingLeft: '12px',
          paddingRight: '12px'
        }}>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
            <div
              className="gear-icon"
              onClick={openManageServers}
              style={{
                position: 'static',
                right: 'auto',
                top: 'auto',
                fontSize: '16px',
                color: 'white',
                textDecoration: 'none',
                cursor: 'pointer'
              }}
            >
              <FontAwesomeIcon icon={faCog} />
            </div>
          </div>
          <div className="copyright-text" style={{ margin: 0 }}>
            © 2026 Stage One Education, LLC
          </div>
          <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <ResetAllButton callback={resetAll} size="sm" style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }} />
          </div>
        </div>
      </Container>

    </div>
  );
}

export { BaseApp };
